import React, { useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { BookOpen, Search } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";

const BreedInfo = () => {
  const { themeClasses } = useTheme();
  const [breedName, setBreedName] = useState("");
  const [breedInfo, setBreedInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("basic");

  const getBreedInfo = httpsCallable(functions, "getBreedInfo");

  const handleGetBreedInfo = async () => {
    if (!breedName.trim()) {
      setError("Please enter a breed name");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result = await getBreedInfo({ breed: breedName });
      setBreedInfo(result.data);
    } catch (err) {
      setError(err.message || "Failed to get breed information");
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: "basic", label: "Basic Info", icon: "🐕" },
    { id: "exercise", label: "Exercise", icon: "🏃" },
    { id: "nutrition", label: "Nutrition", icon: "🥘" },
    { id: "health", label: "Health", icon: "🏥" },
    { id: "temperament", label: "Temperament", icon: "😊" },
    { id: "grooming", label: "Grooming", icon: "✂️" },
    { id: "training", label: "Training", icon: "🎓" },
    { id: "living", label: "Living", icon: "🏠" },
    { id: "family", label: "Family", icon: "👪" },
    { id: "costs", label: "Costs", icon: "💰" },
  ];

  const renderTabContent = () => {
    if (!breedInfo?.info) return null;

    const info = breedInfo.info[activeTab];
    if (!info)
      return (
        <div className={themeClasses.text.secondary}>
          No information available for this category.
        </div>
      );

    return (
      <div className="space-y-4">
        {Object.entries(info).map(([key, value]) => (
          <div
            key={key}
            className={`border-b ${themeClasses.border.primary} pb-3`}
          >
            <dt
              className={`font-medium ${themeClasses.text.accent} capitalize mb-1`}
            >
              {key.replace(/_/g, " ")}
            </dt>
            <dd className={themeClasses.text.primary}>
              {Array.isArray(value) ? (
                <ul className="list-disc list-inside space-y-1 ml-4">
                  {value.map((item, index) => (
                    <li key={index} className={themeClasses.text.primary}>
                      {item}
                    </li>
                  ))}
                </ul>
              ) : typeof value === "object" ? (
                <div className="ml-4 space-y-2">
                  {Object.entries(value).map(([subKey, subValue]) => (
                    <div key={subKey}>
                      <span
                        className={`font-medium ${themeClasses.text.accent} capitalize`}
                      >
                        {subKey.replace(/_/g, " ")}:
                      </span>{" "}
                      <span className={themeClasses.text.primary}>
                        {Array.isArray(subValue)
                          ? subValue.join(", ")
                          : subValue}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <span className={themeClasses.text.primary}>{value}</span>
              )}
            </dd>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3 mb-6">
        <BookOpen className={`w-8 h-8 ${themeClasses.text.accent}`} />
        <div>
          <h2 className={`text-2xl font-bold ${themeClasses.text.primary}`}>
            Comprehensive Breed Information
          </h2>
          <p className={themeClasses.text.secondary}>
            Get detailed information about any dog breed including health,
            exercise, nutrition, and more!
          </p>
        </div>
      </div>

      <div
        className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6`}
      >
        {/* Search Section */}
        <div className="mb-6">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search
                className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${themeClasses.text.secondary}`}
              />
              <input
                type="text"
                value={breedName}
                onChange={(e) => setBreedName(e.target.value)}
                placeholder="Enter breed name (e.g., Golden Retriever, Labrador, German Shepherd)"
                className={`w-full pl-10 pr-4 py-3 ${themeClasses.bg.input} border ${themeClasses.border.primary} rounded-lg ${themeClasses.text.primary} placeholder-gray-400 focus:border-cyan-400 focus:outline-none`}
                onKeyPress={(e) => e.key === "Enter" && handleGetBreedInfo()}
              />
            </div>
            <button
              onClick={handleGetBreedInfo}
              disabled={loading}
              className={`px-6 py-3 ${themeClasses.bg.button} text-white font-medium rounded-lg transition-all duration-300 disabled:opacity-50`}
            >
              {loading ? "Analyzing..." : "Get Info"}
            </button>
          </div>
          {error && <p className="text-red-400 mt-2 text-sm">{error}</p>}
        </div>

        {/* Results Section */}
        {breedInfo && (
          <div className="space-y-6">
            {/* Header */}
            <div
              className={`${themeClasses.bg.secondary} rounded-lg p-4 border ${themeClasses.border.primary}`}
            >
              <h3 className={`text-xl font-bold ${themeClasses.text.primary}`}>
                {breedInfo.breed}
              </h3>
              {breedInfo.note && (
                <p className={`${themeClasses.text.secondary} text-sm mt-1`}>
                  {breedInfo.note}
                </p>
              )}
            </div>

            {/* Tabs */}
            <div className={`border-b ${themeClasses.border.primary}`}>
              <nav className="-mb-px flex space-x-4 overflow-x-auto">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`${
                      activeTab === tab.id
                        ? `border-cyan-400 ${themeClasses.text.accent}`
                        : `border-transparent ${themeClasses.text.secondary} hover:${themeClasses.text.primary} hover:border-gray-600`
                    } whitespace-nowrap py-2 px-3 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors`}
                  >
                    <span>{tab.icon}</span>
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div
              className={`${themeClasses.bg.secondary} rounded-lg border ${themeClasses.border.primary} p-6 min-h-[400px]`}
            >
              {renderTabContent()}
            </div>
          </div>
        )}

        {/* Sample Breeds */}
        <div
          className={`mt-8 ${themeClasses.bg.secondary} rounded-lg p-4 border ${themeClasses.border.primary}`}
        >
          <h4 className={`font-medium ${themeClasses.text.primary} mb-3`}>
            Try these popular breeds:
          </h4>
          <div className="flex flex-wrap gap-2">
            {[
              "Golden Retriever",
              "Labrador Retriever",
              "German Shepherd",
              "French Bulldog",
              "Mixed Breed",
            ].map((breed) => (
              <button
                key={breed}
                onClick={() => {
                  setBreedName(breed);
                  // Auto-search after setting name
                  setTimeout(() => {
                    handleGetBreedInfo();
                  }, 100);
                }}
                className={`px-3 py-1 ${themeClasses.bg.buttonSecondary} border ${themeClasses.border.primary} rounded-full text-sm ${themeClasses.text.primary} hover:${themeClasses.bg.hover} transition-colors`}
              >
                {breed}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BreedInfo;
